import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Upload, Check, ShieldCheck, Info } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FORM_OPTIONS } from "../lib/mockData";

const STEPS = [
  { num: 1, label: "About you" },
  { num: 2, label: "Your innovation" },
  { num: 3, label: "Preferences" },
  { num: 4, label: "Review" },
];

export default function Submit() {
  const [step, setStep] = useState(2);
  const [entityType, setEntityType] = useState("Startup (DPIIT)");
  const [patentType, setPatentType] = useState("Utility Patent");
  const [selectedLangs, setSelectedLangs] = useState(["English", "Hindi"]);

  const toggleLang = (l) => setSelectedLangs((prev) => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);

  return (
    <div className="surface-cream min-h-screen" data-testid="submit-page">
      <Header />

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-[280px_1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="chip mb-4">Submission</div>
          <h1 className="font-display font-bold text-2xl text-[#0A2520] mb-1">Tell us about your innovation</h1>
          <p className="text-sm text-[#5A6B66] mb-8">~3 minutes · saved automatically</p>
          <div className="space-y-1">
            {STEPS.map((s) => {
              const active = s.num === step;
              const done = s.num < step;
              return (
                <button key={s.num} onClick={() => setStep(s.num)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition ${active ? "surface-sage" : "hover:bg-[#F0F4F0]"}`}
                  data-testid={`step-${s.num}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done || active ? "bg-[#0E6E5E] text-white" : "bg-[#F0F4F0] text-[#5A6B66]"}`}>
                    {done ? <Check size={14} /> : s.num}
                  </div>
                  <div className={`font-display text-sm font-semibold ${active ? "text-[#0E6E5E]" : "text-[#0A2520]"}`}>{s.label}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-10 card-flat p-4">
            <div className="flex gap-2 mb-2">
              <ShieldCheck size={16} className="text-[#0E6E5E] flex-shrink-0"/>
              <div className="font-display text-sm font-semibold text-[#0A2520]">Your idea stays private</div>
            </div>
            <p className="text-xs text-[#5A6B66] leading-relaxed">All briefs are confidential. Attorneys sign NDAs before viewing details.</p>
          </div>
        </aside>

        <section className="card-flat p-8 lg:p-10" data-testid="submit-form">
          <div className="lg:hidden mb-6">
            <div className="flex items-center justify-between mb-2">
              {STEPS.map((s) => <div key={s.num} className={`flex-1 h-1 mx-0.5 rounded-full ${s.num <= step ? "bg-[#0E6E5E]" : "bg-[#E1E8E2]"}`}/>)}
            </div>
            <div className="text-sm text-[#5A6B66]">Step {step}: {STEPS[step-1].label}</div>
          </div>

          <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">Your innovation</h2>
          <p className="text-[#5A6B66] mb-8">A short brief is enough — attorneys will request more under NDA.</p>

          <Field label="What kind of protection?">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {FORM_OPTIONS.patentTypes.map((t) => (
                <button key={t} onClick={() => setPatentType(t)}
                  className={`text-left p-3.5 rounded-xl border text-sm font-semibold transition ${patentType === t ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}
                  data-testid={`patent-${t}`}>{t}</button>
              ))}
            </div>
          </Field>

          <Field label="I'm filing as">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {FORM_OPTIONS.entityTypes.map((t) => (
                <button key={t} onClick={() => setEntityType(t)}
                  className={`text-left p-3.5 rounded-xl border text-sm font-semibold transition ${entityType === t ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}
                  data-testid={`entity-${t}`}>{t}</button>
              ))}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-5 mb-7">
            <Field label="Industry / Domain" noMargin>
              <select className="input" data-testid="industry">{FORM_OPTIONS.industries.map((i) => <option key={i}>{i}</option>)}</select>
            </Field>
            <Field label="City" noMargin>
              <select className="input" data-testid="city">{FORM_OPTIONS.cities.map((i) => <option key={i}>{i}</option>)}</select>
            </Field>
          </div>

          <Field label="Preferred language(s) for consultation">
            <div className="flex flex-wrap gap-2">
              {FORM_OPTIONS.languages.map((l) => (
                <button key={l} onClick={() => toggleLang(l)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${selectedLangs.includes(l) ? "bg-[#0E6E5E] text-white" : "bg-[#EBF3EC] text-[#0B5447] hover:bg-[#D5E2D6]"}`}
                  data-testid={`lang-${l}`}>{l}</button>
              ))}
            </div>
          </Field>

          <Field label="Working title">
            <input defaultValue="AI-powered crop disease detection system" className="input" data-testid="title" />
          </Field>

          <Field label="Brief description">
            <textarea rows={5} defaultValue="A device-edge ML model that identifies 14 common crop diseases from smartphone-captured leaf images and recommends treatment to small farmers. Includes a novel preprocessing pipeline that compensates for varied lighting in Indian field conditions."
              className="input resize-none leading-relaxed" data-testid="description" />
            <div className="flex items-center gap-1.5 text-xs text-[#5A6B66] mt-2">
              <Info size={12}/> 50-500 words is ideal. Avoid disclosing trade secrets.
            </div>
          </Field>

          <Field label="Attachments (optional)">
            <div className="border-2 border-dashed border-[#DDE5DD] rounded-2xl p-8 text-center surface-sage hover:border-[#0E6E5E] transition cursor-pointer" data-testid="upload">
              <Upload size={28} className="mx-auto text-[#0E6E5E] mb-3"/>
              <div className="font-display font-semibold text-sm text-[#0A2520] mb-1">Drop diagrams, drafts, or images</div>
              <div className="text-xs text-[#5A6B66]">PDF, DOCX, PNG, JPG · up to 25MB</div>
            </div>
          </Field>

          <div className="flex items-center justify-between pt-6 border-t border-[#E1E8E2]">
            <button onClick={() => setStep(Math.max(1, step - 1))} className="btn-outline" data-testid="submit-back">
              <ArrowLeft size={16}/> Back
            </button>
            <Link to="/compare" className="btn-primary" data-testid="submit-next">
              Get quotes <ArrowRight size={16}/>
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

function Field({ label, children, noMargin }) {
  return (
    <div className={noMargin ? "" : "mb-7"}>
      <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2.5">{label}</label>
      {children}
    </div>
  );
}
